
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Form, Table, Modal, Badge } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { formatDateOnlyPtBr, getLocalDateOnly, normalizeDateOnly } from "../../utils/dateOnly";

const GerenciamentoNotas = ({ turmaId, alunos }) => {
    const [notas, setNotas] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingNota, setDeletingNota] = useState(null);
    const [editingNota, setEditingNota] = useState(null);
    const [formData, setFormData] = useState({
        alunoId: '',
        data: getLocalDateOnly(),
        notaWorkbook: '',
        notaProva: ''
    });
    const [alunoMenor, setAlunoMenor] = useState(false);

    useEffect(() => {
        if (turmaId) {
            carregarNotas();
        }
    }, [turmaId]);

    const carregarNotas = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/notas/turma/${turmaId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setNotas(response.data);
        } catch (error) {
            console.error("Erro ao carregar notas:", error);
            toast.error("Erro ao carregar notas.");
        }
    };

    const verificarMaioridade = async (alunoId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/aluno/${alunoId}/datanascimento`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const nascimento = new Date(response.data.cp_datanascimento);
            const hoje = new Date();
            let idade = hoje.getFullYear() - nascimento.getFullYear();
            const mes = hoje.getMonth() - nascimento.getMonth();
            if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
                idade--;
            }
            setAlunoMenor(idade < 18);
        } catch (error) {
            console.error("Erro ao verificar idade do aluno:", error);
            setAlunoMenor(false);
        }
    };

    const handleAlunoChange = (alunoId) => {
        setFormData({ ...formData, alunoId });
        if (alunoId) {
            verificarMaioridade(alunoId);
        } else {
            setAlunoMenor(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.notaProva) {
            toast.error("É obrigatório preencher a nota da prova!");
            return;
        }

        if (alunoMenor && !formData.notaWorkbook) {
            toast.error("É obrigatório preencher a nota do workbook para alunos menores de 18 anos!");
            return;
        }

        if (parseFloat(formData.notaProva) < 0 || parseFloat(formData.notaProva) > 10) {
            toast.error("A nota da prova deve estar entre 0 e 10!");
            return;
        }

        if (formData.notaWorkbook && (parseFloat(formData.notaWorkbook) < 0 || parseFloat(formData.notaWorkbook) > 10)) {
            toast.error("A nota do workbook deve estar entre 0 e 10!");
            return;
        }

        if (!editingNota && !formData.alunoId) {
            toast.error("É obrigatório selecionar um aluno!");
            return;
        }

        // Abrir modal de confirmação
        setShowConfirmModal(true);
    };

    const handleConfirmSave = async () => {
        try {
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
            const workbookPayload = formData.notaWorkbook && formData.notaWorkbook !== '' && parseFloat(formData.notaWorkbook) > 0 ? formData.notaWorkbook : 0;

            if (editingNota) {
                // Atualizar nota existente
                await axios.put(`${API_BASE_URL}/notas/${editingNota.cp_nota_id}`, {
                    data: formData.data,
                    notaWorkbook: workbookPayload,
                    notaProva: formData.notaProva
                }, { headers });
                toast.success("Nota atualizada com sucesso!");
            } else {
                // Criar nova nota
                await axios.post(`${API_BASE_URL}/notas`, {
                    turmaId: turmaId,
                    alunoId: formData.alunoId,
                    data: formData.data,
                    notaWorkbook: workbookPayload,
                    notaProva: formData.notaProva
                }, { headers });
                toast.success("Nota salva com sucesso!");
            }

            setShowModal(false);
            setShowConfirmModal(false);
            resetForm();
            carregarNotas();
        } catch (error) {
            console.error("Erro ao salvar nota:", error);
            toast.error("Erro ao salvar nota.");
            setShowConfirmModal(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (deletingNota) {
            try {
                await axios.delete(`${API_BASE_URL}/notas/${deletingNota}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                toast.success("Nota deletada com sucesso!");
                setShowDeleteModal(false);
                setDeletingNota(null);
                carregarNotas();
            } catch (error) {
                console.error("Erro ao deletar nota:", error);
                toast.error("Erro ao deletar nota.");
                setShowDeleteModal(false);
                setDeletingNota(null);
            }
        }
    };

    const handleDelete = (notaId) => {
        setDeletingNota(notaId);
        setShowDeleteModal(true);
    };

    const handleEdit = (nota) => {
        setEditingNota(nota);
        setFormData({
            alunoId: nota.cp_nota_aluno_id,
            data: normalizeDateOnly(nota.cp_nota_data),
            notaWorkbook: nota.cp_nota_workbook,
            notaProva: nota.cp_nota_prova
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            alunoId: '',
            data: getLocalDateOnly(),
            notaWorkbook: '',
            notaProva: ''
        });
        setEditingNota(null);
        setAlunoMenor(false);
    };

    const getNotaColor = (media) => {
        if (media >= 7) return "success"; // Verde
        if (media >= 5) return "warning"; // Amarelo
        return "danger"; // Vermelho
    };

    const getNotaText = (media) => {
        if (media >= 7) return "Boa";
        if (media >= 5) return "Média";
        return "Ruim";
    };

    const getAlunoNome = (alunoId) => {
        const aluno = alunos.find(a => a.cp_id === parseInt(alunoId));
        return aluno ? aluno.cp_nome : 'Aluno não encontrado';
    };

    return (
        <div className="mt-2 notas-section">
            <div className="bg-transparent">
                <div className="d-flex justify-content-between align-items-center p-2 mb-2 border-bottom bg-theme-secondary-subtle">
                    <div>
                        <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                            <Icon icon="mdi:school-outline" className="text-primary" />
                            Gerenciamento de Notas
                        </h5>
                        <small className="text-muted">Acompanhe provas e workbook.</small>
                    </div>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowModal(true)}
                        disabled={!alunos.length}
                        className="rounded-pill px-4 shadow-sm"
                    >
                        Adicionar Nota
                    </Button>
                </div>
                <div className="p-0">
                    {notas.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <div className="mb-2">Nenhuma nota registrada para esta turma.</div>
                            <small>Quando adicionar, elas aparecerao aqui.</small>
                        </div>
                    ) : (
                        <Table responsive striped hover className="notas-table align-middle">
                            <thead className="sala-table-head">
                                <tr>
                                    <th>Data</th>
                                    <th>Aluno</th>
                                    <th>Workbook</th>
                                    <th>Prova</th>
                                    <th>Média</th>
                                    <th>Status</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notas.map((nota) => (
                                    <tr key={nota.cp_nota_id}>
                                        <td>{formatDateOnlyPtBr(nota.cp_nota_data)}</td>
                                        <td>{nota.cp_nome_aluno}</td>
                                        <td>{nota.cp_nota_workbook}</td>
                                        <td>{nota.cp_nota_prova}</td>
                                        <td>{nota.cp_nota_media}</td>
                                        <td>
                                            <Badge bg={getNotaColor(nota.cp_nota_media)}>
                                                {getNotaText(nota.cp_nota_media)}
                                            </Badge>
                                        </td>
                                        <td className="text-end">
                                            <Button
                                                size="sm"
                                                variant="light"
                                                className="btn-icon rounded-circle text-primary border-0 me-1"
                                                onClick={() => handleEdit(nota)}
                                                title="Editar"
                                            >
                                                <Icon icon="mdi:pencil" width="18" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="light"
                                                className="btn-icon rounded-circle text-danger border-0"
                                                onClick={() => handleDelete(nota.cp_nota_id)}
                                                title="Excluir"
                                            >
                                                <Icon icon="mdi:trash-can-outline" width="18" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </div>
            </div>

            {/* Modal para adicionar/editar nota */}
            <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingNota ? 'Editar Nota' : 'Adicionar Nova Nota'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Aluno *</Form.Label>
                                    <Form.Select
                                        value={formData.alunoId}
                                        onChange={(e) => setFormData({...formData, alunoId: e.target.value})}
                                        required
                                        disabled={editingNota}
                                    >
                                        <option value="">Selecione um aluno</option>
                                        {alunos.map((aluno) => (
                                            <option key={aluno.cp_id} value={aluno.cp_id}>
                                                {aluno.cp_nome}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data da Avaliação *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.data}
                                        onChange={(e) => setFormData({...formData, data: e.target.value})}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nota Workbook (0-10)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="10"
                                        value={formData.notaWorkbook}
                                        onChange={(e) => setFormData({...formData, notaWorkbook: e.target.value})}
                                        placeholder="Ex: 8.5"
                                    />
                                    <Form.Text className="text-muted">
                                        Trabalho feito um dia antes da prova
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nota Prova (0-10) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="10"
                                        value={formData.notaProva}
                                        onChange={(e) => setFormData({...formData, notaProva: e.target.value})}
                                        required
                                        placeholder="Ex: 7.0"
                                    />
                                    <Form.Text className="text-muted">
                                        Nota da prova aplicada
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        
                        {formData.notaWorkbook && parseFloat(formData.notaWorkbook) > 0 && formData.notaProva && (
                            <div className="alert alert-info">
                                <strong>Média Calculada: </strong>
                                {((parseFloat(formData.notaWorkbook) + parseFloat(formData.notaProva)) / 2).toFixed(1)}
                                <br />
                                <small>
                                    <Badge bg={getNotaColor((parseFloat(formData.notaWorkbook) + parseFloat(formData.notaProva)) / 2)}>
                                        {getNotaText((parseFloat(formData.notaWorkbook) + parseFloat(formData.notaProva)) / 2)}
                                    </Badge>
                                </small>
                            </div>
                        )}
                        {formData.notaProva && (!formData.notaWorkbook || parseFloat(formData.notaWorkbook) === 0 || formData.notaWorkbook === '') && (
                            <div className="alert alert-info">
                                <strong>Nota: </strong>
                                {parseFloat(formData.notaProva).toFixed(1)}
                                <br />
                                <small>
                                    <Badge bg={getNotaColor(parseFloat(formData.notaProva))}>
                                        {getNotaText(parseFloat(formData.notaProva))}
                                    </Badge>
                                </small>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            {editingNota ? 'Atualizar' : 'Salvar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de Confirmação */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar {editingNota ? 'Atualização' : 'Cadastro'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Deseja mesmo {editingNota ? 'atualizar' : 'cadastrar'} esta nota?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleConfirmSave}>
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de confirmação para exclusão */}
            <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setDeletingNota(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Exclusão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Tem certeza que deseja excluir esta nota?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setDeletingNota(null); }}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm}>
                        Excluir
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default GerenciamentoNotas;
